import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../layout/AdminLayout';
import { StatsCard } from '../common/StatsCard';
import { PageHeader } from '../common/PageHeader';
import { StatusBadge } from '../common/StatusBadge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { statsApi, seminarApi, bookingApi } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import {
    Users,
    Award,
    Calendar,
    BookOpen,
    Receipt,
    AlertTriangle,
    ArrowRight,
    Loader2,
    Database,
    Clock,
    XCircle,
    ShieldCheck,
    CheckCircle2,
    HeartHandshake,
    FileText,
    BookOpenCheck,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function DashboardPage() {
    const [stats, setStats] = useState(null);
    const [recentSeminars, setRecentSeminars] = useState([]);
    const [recentBookings, setRecentBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [renewalFilter, setRenewalFilter] = useState('all'); // 'all' | 'ready' | 'incomplete'
    const navigate = useNavigate();

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [statsRes, seminarsRes, bookingsRes] = await Promise.all([
                statsApi.getDashboard(),
                seminarApi.getAll(),
                bookingApi.getAll(),
            ]);
            setStats(statsRes.data);
            setRecentSeminars(seminarsRes.data.slice(0, 5));
            // Verwaiste Buchungen (ohne Teilnehmer-Zuordnung) werden aus dem
            // Dashboard ausgeblendet — sie haben keinen aussagekräftigen Eintrag.
            const validBookings = (bookingsRes.data || []).filter(b => !!b.participant_name);
            // Nach Erstellzeitpunkt absteigend sortieren — neueste Buchung zuerst
            validBookings.sort((a, b) => {
                const tA = new Date(a.created_at || 0).getTime();
                const tB = new Date(b.created_at || 0).getTime();
                return tB - tA;
            });
            setRecentBookings(validBookings.slice(0, 5));
        } catch (error) {
            toast.error('Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </AdminLayout>
        );
    }

    const hasLicenseWarnings = (stats?.expiring_licenses || 0) > 0 || (stats?.expired_licenses_count || 0) > 0;
    const pendingReviewCount = stats?.pending_review_licenses_count || 0;
    const pendingReviewLicenses = stats?.pending_review_license_details || [];
    const pendingRenewalCount = stats?.pending_renewals_count || 0;
    const pendingRenewals = stats?.pending_renewal_details || [];
    const pendingTrainingRecordCount = stats?.pending_training_records_count || 0;
    const pendingTrainingRecords = stats?.pending_training_record_details || [];

    return (
        <AdminLayout>
            <PageHeader
                title="Dashboard"
                description="Übersicht über Ihr BGPR Verwaltungssystem"
            />

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="stats-grid">
                <StatsCard
                    title="Akademie-Mitglieder"
                    value={stats?.active_instructors || 0}
                    icon={Users}
                    accentColor
                />
                <StatsCard
                    title="Aktive Lizenzen"
                    value={stats?.active_licenses || 0}
                    icon={Award}
                />
                <StatsCard
                    title="Aktive Fortbildungen"
                    value={stats?.active_seminars || 0}
                    icon={Calendar}
                />
                <StatsCard
                    title="Offene Rechnungen"
                    value={stats?.open_invoices || 0}
                    icon={Receipt}
                />
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="secondary-stats">
                <MiniStat label="Ausstehende Buchungen" value={stats?.pending_bookings || 0} />
                <MiniStat label="Bestätigte Buchungen" value={stats?.confirmed_bookings || 0} />
                <MiniStat label="Bezahlte Rechnungen" value={stats?.paid_invoices || 0} />
                <MiniStat label="Archivierte Mitglieder" value={stats?.archived_instructors || 0} />
            </div>

            {/* License Review Inbox */}
            {pendingReviewCount > 0 && (
                <Card className="border-blue-200 bg-blue-50/50 mb-6" data-testid="dashboard-review-inbox">
                    <CardHeader className="pb-2">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <ShieldCheck className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="flex-1">
                                <CardTitle className="text-base text-blue-900">
                                    {pendingReviewCount} Lizenz(en) zur Prüfung eingereicht
                                </CardTitle>
                                <p className="text-xs text-blue-700 mt-0.5">Von Akademie-Mitgliedern hochgeladene Bestands-Lizenzen warten auf Ihre Freigabe.</p>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                className="border-blue-300 text-blue-700 hover:bg-blue-100"
                                onClick={() => navigate('/admin/licenses')}
                                data-testid="view-pending-review-licenses"
                            >
                                Prüf-Inbox öffnen <ArrowRight className="ml-1 h-3 w-3" />
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {pendingReviewLicenses.slice(0, 5).map((lic) => (
                                <div
                                    key={lic.license_id}
                                    className="flex items-center justify-between bg-white px-3 py-2 rounded-md border border-blue-100 hover:bg-blue-50 cursor-pointer"
                                    onClick={() => navigate(`/admin/instructors/${lic.instructor_id}`)}
                                    data-testid={`pending-review-row-${lic.license_id}`}
                                >
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">{lic.instructor_name}</p>
                                        <p className="text-xs text-gray-500 truncate">
                                            {lic.license_type} &middot; Nr. {lic.license_number || '—'} &middot; Gültig bis: {formatDate(lic.valid_until)}
                                        </p>
                                    </div>
                                    <ArrowRight className="h-4 w-4 text-blue-400 shrink-0" />
                                </div>
                            ))}
                            {pendingReviewCount > 5 && (
                                <p className="text-xs text-blue-600 text-center pt-1">… und {pendingReviewCount - 5} weitere</p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Pending License Renewals */}
            {pendingRenewalCount > 0 && (() => {
                const readyCount      = pendingRenewals.filter(r => r.ready_to_approve).length;
                const incompleteCount = pendingRenewals.length - readyCount;
                const filtered =
                    renewalFilter === 'ready'      ? pendingRenewals.filter(r => r.ready_to_approve) :
                    renewalFilter === 'incomplete' ? pendingRenewals.filter(r => !r.ready_to_approve) :
                    pendingRenewals;
                const tabs = [
                    { value: 'all',        label: 'Alle',          count: pendingRenewals.length },
                    { value: 'ready',      label: 'Bereit',        count: readyCount },
                    { value: 'incomplete', label: 'Unvollständig', count: incompleteCount },
                ];
                return (
                <Card className="border-amber-200 bg-amber-50/50 mb-6" data-testid="dashboard-pending-renewals">
                    <CardHeader className="pb-2">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-amber-100 rounded-lg">
                                <ShieldCheck className="h-5 w-5 text-amber-600" />
                            </div>
                            <div className="flex-1">
                                <CardTitle className="text-base text-amber-900">
                                    {pendingRenewalCount} offene(r) Lizenzverlängerungs-Antrag/Anträge
                                </CardTitle>
                                <p className="text-xs text-amber-700 mt-0.5">
                                    Vorprüfung: <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-600 inline" /> bereit</span> · <span className="inline-flex items-center gap-1"><XCircle className="h-3 w-3 text-red-500 inline" /> Anforderung fehlt</span>. Klicken öffnet das Antrags-Profil.
                                </p>
                            </div>
                            {pendingRenewals.length > 1 && (
                                <div
                                    className="inline-flex rounded-md border border-amber-200 bg-white p-0.5 text-xs shrink-0"
                                    role="tablist"
                                    data-testid="renewal-filter-tabs"
                                >
                                    {tabs.map(tab => (
                                        <button
                                            key={tab.value}
                                            type="button"
                                            role="tab"
                                            aria-selected={renewalFilter === tab.value}
                                            onClick={() => setRenewalFilter(tab.value)}
                                            className={`px-2.5 py-1 rounded-sm font-medium transition-colors ${
                                                renewalFilter === tab.value
                                                    ? 'bg-amber-100 text-amber-900 shadow-sm'
                                                    : 'text-amber-700 hover:text-amber-900'
                                            }`}
                                            data-testid={`renewal-filter-${tab.value}`}
                                        >
                                            {tab.label}
                                            <span className="ml-1 opacity-70">({tab.count})</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {filtered.length === 0 ? (
                                <p className="text-xs text-center text-amber-700 py-4 italic" data-testid="renewal-filter-empty">
                                    {renewalFilter === 'ready' && 'Aktuell kein Antrag „bereit" — alle haben noch Lücken.'}
                                    {renewalFilter === 'incomplete' && 'Keine unvollständigen Anträge — alle bereit zur Genehmigung. 🎉'}
                                </p>
                            ) : filtered.slice(0, 5).map((r) => {
                                const triage = [
                                    { ok: r.ue_ok,           Icon: BookOpenCheck,  label: 'UE',          tip: r.ue_required > 0 ? `${r.ue_value} / ${r.ue_required} UE` : 'UE' },
                                    { ok: r.first_aid_ok,    Icon: HeartHandshake, label: 'Erste-Hilfe', tip: r.first_aid_ok ? 'aktuell' : 'fehlt / abgelaufen' },
                                    { ok: r.license_pdf_ok,  Icon: FileText,       label: 'Lizenz-PDF',  tip: r.license_pdf_ok ? 'vorhanden' : 'fehlt' },
                                ];
                                const ready = r.ready_to_approve;
                                return (
                                    <div
                                        key={r.renewal_id}
                                        className={`flex items-center justify-between gap-3 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                                            ready
                                                ? 'bg-emerald-50/70 border-emerald-200 hover:bg-emerald-100/70'
                                                : 'bg-white border-amber-100 hover:bg-amber-50'
                                        }`}
                                        onClick={() => navigate(`/admin/instructors/${r.instructor_id}`)}
                                        data-testid={`pending-renewal-row-${r.renewal_id}`}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="text-sm font-medium text-gray-900 truncate">{r.instructor_name}</p>
                                                {ready && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid={`renewal-ready-${r.renewal_id}`}>
                                                        <CheckCircle2 className="h-2.5 w-2.5" />
                                                        Bereit
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 truncate">
                                                {r.instructor_email} &middot; beantragt: {formatDate(r.created_at?.split('T')[0])}
                                                {r.tier_label ? <> &middot; <span className="italic">{r.tier_label}</span></> : null}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            {triage.map(({ ok, Icon, label, tip }) => (
                                                <span
                                                    key={label}
                                                    title={`${label}: ${tip}`}
                                                    className={`inline-flex items-center justify-center h-6 w-6 rounded-md border ${
                                                        ok ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : 'bg-red-50 border-red-200 text-red-500'
                                                    }`}
                                                    data-testid={`triage-${label.toLowerCase().replace(/[^a-z]+/g, '-')}-${r.renewal_id}`}
                                                >
                                                    <Icon className="h-3.5 w-3.5" />
                                                </span>
                                            ))}
                                        </div>
                                        <ArrowRight className="h-4 w-4 text-amber-500 shrink-0 ml-1" />
                                    </div>
                                );
                            })}
                            {filtered.length > 5 && (
                                <p className="text-xs text-amber-700 text-center pt-1">… und {filtered.length - 5} weitere</p>
                            )}
                        </div>
                    </CardContent>
                </Card>
                );
            })()}

            {/* Pending Training Records */}
            {pendingTrainingRecordCount > 0 && (
                <Card className="border-indigo-200 bg-indigo-50/50 mb-6" data-testid="dashboard-pending-training-records">
                    <CardHeader className="pb-2">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-indigo-100 rounded-lg">
                                <ShieldCheck className="h-5 w-5 text-indigo-600" />
                            </div>
                            <div className="flex-1">
                                <CardTitle className="text-base text-indigo-900">
                                    {pendingTrainingRecordCount} externe(r) Fortbildungsnachweis/e zu prüfen
                                </CardTitle>
                                <p className="text-xs text-indigo-700 mt-0.5">Nicht-BGPR-Fortbildungen warten auf Anerkennung, Teilanerkennung oder Ablehnung.</p>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {pendingTrainingRecords.slice(0, 5).map((t) => (
                                <div
                                    key={t.record_id}
                                    className="flex items-center justify-between bg-white px-3 py-2 rounded-md border border-indigo-100 hover:bg-indigo-50 cursor-pointer"
                                    onClick={() => navigate(`/admin/instructors/${t.instructor_id}`)}
                                    data-testid={`pending-training-record-row-${t.record_id}`}
                                >
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                            {t.instructor_name} – {t.name}
                                        </p>
                                        <p className="text-xs text-gray-500 truncate">
                                            {t.ue_count} UE &middot; ausgestellt: {formatDate(t.date_issued)}
                                            {t.record_type === 'first_aid' && ' · 1. Hilfe'}
                                        </p>
                                    </div>
                                    <ArrowRight className="h-4 w-4 text-indigo-400 shrink-0" />
                                </div>
                            ))}
                            {pendingTrainingRecordCount > 5 && (
                                <p className="text-xs text-indigo-700 text-center pt-1">… und {pendingTrainingRecordCount - 5} weitere</p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* License Warnings */}
            {hasLicenseWarnings && (
                <div className="space-y-4 mb-6" data-testid="license-warnings">
                    {/* Expired licenses */}
                    {(stats?.expired_licenses_count || 0) > 0 && (
                        <Card className="border-red-200 bg-red-50/50">
                            <CardHeader className="pb-2">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-red-100 rounded-lg">
                                        <XCircle className="h-5 w-5 text-red-600" />
                                    </div>
                                    <div className="flex-1">
                                        <CardTitle className="text-base text-red-800">
                                            {stats.expired_licenses_count} abgelaufene Lizenz(en)
                                        </CardTitle>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="border-red-300 text-red-700 hover:bg-red-100"
                                        onClick={() => navigate('/admin/licenses')}
                                        data-testid="view-expired-licenses"
                                    >
                                        Alle anzeigen <ArrowRight className="ml-1 h-3 w-3" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {stats.expired_license_details?.slice(0, 5).map((lic) => (
                                        <LicenseWarningRow
                                            key={lic.license_id}
                                            lic={lic}
                                            variant="expired"
                                            navigate={navigate}
                                        />
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Expiring soon licenses */}
                    {(stats?.expiring_licenses || 0) > 0 && (
                        <Card className="border-amber-200 bg-amber-50/50">
                            <CardHeader className="pb-2">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-amber-100 rounded-lg">
                                        <AlertTriangle className="h-5 w-5 text-amber-600" />
                                    </div>
                                    <div className="flex-1">
                                        <CardTitle className="text-base text-amber-800">
                                            {stats.expiring_licenses} Lizenz(en) laufen in den nächsten {stats.warning_days} Tagen ab
                                        </CardTitle>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="border-amber-300 text-amber-700 hover:bg-amber-100"
                                        onClick={() => navigate('/admin/licenses')}
                                        data-testid="view-expiring-licenses"
                                    >
                                        Alle anzeigen <ArrowRight className="ml-1 h-3 w-3" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {stats.expiring_license_details?.slice(0, 5).map((lic) => (
                                        <LicenseWarningRow
                                            key={lic.license_id}
                                            lic={lic}
                                            variant="expiring"
                                            navigate={navigate}
                                        />
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {/* No warnings - success message */}
            {!hasLicenseWarnings && (stats?.total_licenses || 0) > 0 && (
                <Card className="mb-6 border-green-200 bg-green-50/50" data-testid="no-license-warnings">
                    <CardContent className="flex items-center gap-4 p-4">
                        <div className="p-2 bg-green-100 rounded-lg">
                            <ShieldCheck className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                            <p className="font-medium text-green-800">Alle Lizenzen sind gültig</p>
                            <p className="text-sm text-green-600">{stats.active_licenses} aktive Lizenz(en), keine Warnungen</p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Recent Items */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Seminars */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-lg font-heading">Aktuelle Fortbildungen</CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/seminars')} data-testid="view-all-seminars">
                            Alle anzeigen <ArrowRight className="ml-1 h-4 w-4" />
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {recentSeminars.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-4 text-center">Keine Fortbildungen vorhanden</p>
                        ) : (
                            <div className="space-y-3">
                                {recentSeminars.map((seminar) => (
                                    <div
                                        key={seminar.id}
                                        className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                                        onClick={() => navigate(`/admin/seminars/${seminar.id}`)}
                                        data-testid={`seminar-item-${seminar.id}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">{seminar.title}</p>
                                            <p className="text-sm text-muted-foreground">{seminar.date} · {seminar.ue_count} UE</p>
                                        </div>
                                        <StatusBadge status={seminar.is_published ? 'confirmed' : 'pending'} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Recent Bookings */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-lg font-heading">Aktuelle Buchungen</CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/seminars')} data-testid="view-all-bookings">
                            Alle anzeigen <ArrowRight className="ml-1 h-4 w-4" />
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {recentBookings.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-4 text-center">Keine Buchungen vorhanden</p>
                        ) : (
                            <div className="space-y-3">
                                {recentBookings.map((booking) => (
                                    <div
                                        key={booking.id}
                                        className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                                        data-testid={`booking-item-${booking.id}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">
                                                {booking.participant_name || `Buchung #${booking.id.slice(0, 8)}`}
                                            </p>
                                            <p className="text-sm text-muted-foreground truncate">
                                                {booking.seminar_title ? `${booking.seminar_title} · ` : ''}{formatDate(booking.created_at)}
                                            </p>
                                        </div>
                                        <StatusBadge status={booking.status} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </AdminLayout>
    );
}

function MiniStat({ label, value }) {
    return (
        <div className="bg-white rounded-lg border p-4" data-testid={`mini-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
            <p className="text-xl font-bold mt-1">{value}</p>
        </div>
    );
}

function LicenseWarningRow({ lic, variant, navigate }) {
    const isExpired = variant === 'expired';
    return (
        <div
            className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${isExpired ? 'bg-red-100/50 hover:bg-red-100' : 'bg-amber-100/50 hover:bg-amber-100'}`}
            onClick={() => navigate(`/admin/instructors/${lic.instructor_id}`)}
            data-testid={`license-warning-${lic.license_id}`}
        >
            <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${isExpired ? 'bg-red-200 text-red-700' : 'bg-amber-200 text-amber-700'}`}>
                    <Clock className="h-4 w-4" />
                </div>
                <div>
                    <p className="text-sm font-medium">{lic.instructor_name}</p>
                    <p className="text-xs text-muted-foreground">
                        {lic.license_type} · Nr. {lic.license_number} · Gültig bis {lic.valid_until}
                    </p>
                </div>
            </div>
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${isExpired ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'}`}>
                {isExpired ? `${lic.days_overdue} Tage überfällig` : `noch ${lic.days_left} Tage`}
            </span>
        </div>
    );
}
